import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiEdit3,
  FiTrash2,
  FiClock,
  FiArrowLeft,
  FiRefreshCw,
  FiX,
  FiPlus,
} from "react-icons/fi";
import {
  fetchShifts,
  deleteShift,
  updateShift,
} from "../../redux/actions/shiftActions";
import { useNavigate } from "react-router-dom";

const ViewShifts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { shifts, loading } = useSelector((state) => state.shift);

  const [editItem, setEditItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const formatTime = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const h = Number(hours);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
};

  useEffect(() => {
    dispatch(fetchShifts());
  }, [dispatch]);

  const handleEdit = (item) => {
    setEditItem(item);
    setEditData({ ...item });
  };

  const handleUpdate = () => {
    if (!editItem) return;

    const { id, created_at, ...cleanData } = editData;
    dispatch(updateShift(editItem.id, cleanData));
    setEditItem(null);
  };

  const openDelete = (item) => {
    setDeleteItem(item);
    setConfirmDeleteOpen(true);
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          {/* Left */}
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
          >
            <FiArrowLeft />
            Back to Dashboard
          </button>

          {/* Right */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard/create-shift")}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-2 px-4 rounded-xl transition-all shadow-md"
            >
              <FiPlus className="text-lg" />
              Create Shift
            </button>

            <button
              onClick={() => dispatch(fetchShifts())}
              className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
            >
              <FiRefreshCw />
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-8 flex items-center gap-4 text-white">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <FiClock className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Shift Management
              </h1>
              <p className="text-cyan-100/80 text-sm">
                View and adjust company timing schedules
              </p>
            </div>
          </div>

          <div className="p-6 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-widest border-b border-gray-100 dark:border-gray-800">
                  <th className="py-4 px-6">Shift Name</th>
                  <th className="py-4 px-6">Start - End</th>
                  <th className="py-4 px-6">Break</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {shifts.map((s) => (
                  <tr
                    key={s.id}
                    className="group hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-all"
                  >
                    <td className="py-4 px-6 font-semibold dark:text-white">
                      {s.name}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 font-mono font-bold">
                      {formatTime(s.start_time)} - {formatTime(s.end_time)}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500 dark:text-gray-400">
                      {s.break_minutes}m
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${
                          s.status === "active"
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        }`}
                      >
                        {s.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(s)}
                        className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 dark:hover:text-white transition-all"
                      >
                        <FiEdit3 />
                      </button>
                      <button
                        onClick={() => openDelete(s)}
                        className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white dark:hover:bg-red-600 dark:hover:text-white transition-all"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full overflow-hidden border border-white/10 shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-6 flex justify-between items-center text-white">
              <h3 className="font-bold flex items-center gap-2">
                <FiClock /> Edit Shift
              </h3>
              <button onClick={() => setEditItem(null)}>
                <FiX size={22} />
              </button>
            </div>
            <div className="p-8 space-y-4">
              <input
                value={editData.name}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
                className="w-full p-4 rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white border dark:border-gray-700"
                placeholder="Shift Name"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="time"
                  step="1"
                  value={editData.start_time}
                  onChange={(e) =>
                    setEditData({ ...editData, start_time: e.target.value })
                  }
                  className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white border dark:border-gray-700"
                />
                <input
                  type="time"
                  step="1"
                  value={editData.end_time}
                  onChange={(e) =>
                    setEditData({ ...editData, end_time: e.target.value })
                  }
                  className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white border dark:border-gray-700"
                />
              </div>
              <button
                onClick={handleUpdate}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-2xl shadow-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-sm w-full p-8 text-center border border-white/10 shadow-2xl">
            <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <FiTrash2 className="text-red-500 dark:text-red-400 text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Delete Shift?
            </h3>
            <p className="text-gray-500 mb-8">
              Permanently remove{" "}
              <span className="font-bold text-gray-900 dark:text-white">
                "{deleteItem?.name}"
              </span>
              ?
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setConfirmDeleteOpen(false)}
                className="flex-1 py-4 bg-gray-100 dark:bg-gray-800 dark:text-gray-300 rounded-2xl font-bold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  dispatch(deleteShift(deleteItem.id));
                  setConfirmDeleteOpen(false);
                }}
                className="flex-1 py-4 bg-red-500 text-white rounded-2xl font-bold shadow-lg shadow-red-500/30"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewShifts;
